import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Loader2, CheckCircle, Users, Calendar, Paperclip, UserPlus, CalendarDays, ChevronDown } from 'lucide-react';
import { LeaveRequestView, LeaveTypeView, ConflictView, LeaveRequestStatus } from '../../../../models/attendance';
import { getLeaveTypeBadgeStyle } from '../../../../utils/leaveTypeUtils';
import { getFullUrl } from '../../../../utils/url';
import { MiniTeamCalendar } from '../../../../components/leaves/MiniTeamCalendar';

interface ApprovalQueueProps {
  requests: LeaveRequestView[];
  conflicts: Record<number, ConflictView[]>;
  leaveTypes?: LeaveTypeView[];
  onViewRequest: (request: LeaveRequestView) => void;
  onApproveRequest: (requestId: number) => void;
  onRejectRequest: (requestId: number) => void;
  onRequestSelection: (requestId: number, isSelected: boolean) => void;
  selectedRequests: number[];
  isApproving: number | null;
  isRejecting: number | null;
}

const ApprovalQueue: React.FC<ApprovalQueueProps> = ({
  requests,
  conflicts,
  leaveTypes,
  onViewRequest,
  onApproveRequest,
  onRejectRequest,
  onRequestSelection,
  selectedRequests,
  isApproving,
  isRejecting,
}) => {
  const { t } = useTranslation(['leaves', 'common', 'translation']);
  const [expandedCalendar, setExpandedCalendar] = useState<number | null>(null);

  const toggleCalendar = (requestId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCalendar(prev => prev === requestId ? null : requestId);
  };

  const getConflictSeverityColor = (type: string): string => {
    if (type === 'team_overlap') return 'border-orange-500 bg-orange-50';
    if (type === 'blackout') return 'border-red-500 bg-red-50';
    return 'border-yellow-500 bg-yellow-50';
  };

  const formatDate = (dateString: string | Date): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateRange = (startDate: string | Date, endDate: string | Date): string => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    return start === end ? start : `${start} - ${end}`;
  };

  if (!requests?.length) {
    return (
      <div className="text-center py-12" data-testid="empty-queue">
        <div className="w-16 h-16 bg-muted/30 rounded-xl flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">{t('translation:allCaughtUp', 'All Caught Up!')}</h3>
        <p className="text-muted-foreground">{t('translation:noPendingRequests', 'No pending leave requests to review.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((request) => {
        const requestConflicts = conflicts[request.id] || [];
        const isCancellationPending = request.status === LeaveRequestStatus.CancellationPending;
        return (
          <div
            key={request.id}
            data-testid="approval-card"
            className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors cursor-pointer"
            onClick={() => onViewRequest(request)}
          >
            {/* Header with Employee Info */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={selectedRequests.includes(request.id)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.stopPropagation();
                    onRequestSelection(request.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isCancellationPending}
                  title={isCancellationPending ? t('cancellationCannotBulk', 'Cancellation requests cannot be bulk approved') : undefined}
                  data-testid="approval-select"
                  className="rounded border-border text-blue-600 focus-visible:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                  {getFullUrl(request.user?.imageUrl) ? (
                    <img src={getFullUrl(request.user?.imageUrl)!} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">
                      {request.user?.fullName?.charAt(0) || '?'}
                    </span>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-foreground">{request.user?.fullName || t('translation:unknown', 'Unknown')}</h3>
                    {request.isOnBehalf && (
                      <span
                        data-testid="on-behalf-badge"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
                      >
                        <UserPlus size={10} />
                        {request.submittedByName || t('Manager')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{request.user?.title || ''}</p>
                </div>
              </div>
              <div
                className={`px-2 py-1 rounded-full text-xs font-medium ${getLeaveTypeBadgeStyle(request.leaveType, leaveTypes).colorClass}`}
                style={getLeaveTypeBadgeStyle(request.leaveType, leaveTypes).style}
              >
                {request.leaveType}
              </div>
            </div>

            {/* Cancellation Banner */}
            {isCancellationPending && (
              <div className="mb-3 p-2.5 bg-orange-50 border border-orange-200 rounded-lg" data-testid="cancellation-banner">
                <div className="flex items-center gap-2 text-sm text-orange-700 font-medium">
                  <Calendar size={14} className="text-orange-600" />
                  {t('cancellationRequested', 'Cancellation Requested')}
                </div>
                {request.cancelReason && (
                  <p className="text-xs text-orange-600 mt-1 ltr:ml-5 rtl:mr-5">{request.cancelReason}</p>
                )}
              </div>
            )}

            {/* Leave Period */}
            <div className="mb-3 flex items-center gap-2 text-sm">
              <Calendar size={14} className="text-muted-foreground" />
              <span className="font-medium text-foreground">
                {formatDateRange(request.startDate, request.endDate)}
                {request.fromHalfDay && <span className="text-muted-foreground"> ({t('Half day')})</span>}
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">
                {Math.round(request.numberOfDays * 100) / 100} {request.numberOfDays === 1 ? t('translation:day', 'day') : t('translation:days', 'days')}
              </span>
            </div>

            {/* Leave Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('Leave Type')}</label>
                <div
                  className={`inline-flex px-2 py-1 rounded text-xs font-medium mt-1 ${getLeaveTypeBadgeStyle(request.leaveType, leaveTypes).colorClass}`}
                  style={getLeaveTypeBadgeStyle(request.leaveType, leaveTypes).style}
                >
                  {request.leaveType}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('translation:duration', 'Duration')}</label>
                <p className="text-sm font-medium text-foreground mt-1">
                  {Math.round(request.numberOfDays * 100) / 100} {request.numberOfDays === 1 ? t('translation:day', 'day') : t('translation:days', 'days')}
                </p>
              </div>
              {request.dateApplied && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('Date Applied')}</label>
                  <p className="text-sm font-medium text-foreground mt-1">{formatDate(request.dateApplied)}</p>
                </div>
              )}
            </div>

            {/* Comments/Reason */}
            {request.comments && (
              <div className="mb-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('Reason')}</label>
                <p className="text-sm text-foreground mt-1">{request.comments}</p>
              </div>
            )}

            {/* Conflicts */}
            {requestConflicts.length > 0 && (
              <div className="mb-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('translation:conflicts', 'Conflicts')}</label>
                <div className="mt-1 space-y-1">
                  {requestConflicts.map((conflict, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded border-s-4 ${getConflictSeverityColor(conflict.type)}`}
                    >
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        {conflict.type === 'team_overlap' ? (
                          <Users size={14} className="text-muted-foreground" />
                        ) : (
                          <Calendar size={14} className="text-muted-foreground" />
                        )}
                        <span className="text-xs text-foreground">{conflict.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments indicator */}
            {request.attachmentCount > 0 && (
              <div className="mb-3 flex items-center space-x-1 rtl:space-x-reverse text-xs text-blue-600">
                <Paperclip size={12} />
                <span>{request.attachmentCount} {t('Attachments')}</span>
              </div>
            )}

            {/* Mini Team Calendar Toggle + Expansion */}
            <div className="mb-3">
              <button
                onClick={(e) => toggleCalendar(request.id, e)}
                data-testid="toggle-mini-calendar"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-black bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 transition-colors"
              >
                <CalendarDays size={12} />
                {t('leaves:miniCalendarToggle', 'Team Availability')}
                <ChevronDown
                  size={12}
                  className={`transition-transform ${expandedCalendar === request.id ? 'rotate-180' : ''}`}
                />
              </button>
              {expandedCalendar === request.id && (
                <div
                  className="mt-2 border border-border rounded-lg bg-card overflow-hidden"
                  data-testid="mini-calendar-panel"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MiniTeamCalendar
                    leaveStartDate={request.startDate}
                    leaveEndDate={request.endDate}
                  />
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="text-xs text-muted-foreground">
                {formatDate(request.lastUpdate)}
              </div>
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <button
                  onClick={(e) => { e.stopPropagation(); onRejectRequest(request.id); }}
                  disabled={isRejecting === request.id || isApproving === request.id}
                  data-testid="reject-btn"
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-xs rounded transition-colors flex items-center"
                >
                  {isRejecting === request.id ? (
                    <><Loader2 size={12} className="ltr:mr-1 rtl:ml-1 animate-spin" />{t('translation:rejecting', 'Rejecting...')}</>
                  ) : (
                    <><X size={12} className="ltr:mr-1 rtl:ml-1" />{isCancellationPending ? t('rejectCancellation', 'Reject Cancellation') : t('Reject')}</>
                  )}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onApproveRequest(request.id); }}
                  disabled={isApproving === request.id || isRejecting === request.id}
                  data-testid="approve-btn"
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-xs rounded transition-colors flex items-center"
                >
                  {isApproving === request.id ? (
                    <><Loader2 size={12} className="ltr:mr-1 rtl:ml-1 animate-spin" />{t('translation:approving', 'Approving...')}</>
                  ) : (
                    <><Check size={12} className="ltr:mr-1 rtl:ml-1" />{isCancellationPending ? t('approveCancellation', 'Approve Cancellation') : t('Approve')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ApprovalQueue;
